import type { IconSpec } from '@hibro/shared';
import { cn, Icon } from '@hibro/ui-client';
import type { ReactNode } from 'react';

/**
 * Stand-in glyph for a row whose source declared none, so the text of every row
 * starts on the same column — a bare gap made icon-less rows read as indented.
 * The ⌘ mark rather than a neutral blob: the rows that lack an icon are the
 * command ones.
 */
const ROW_FALLBACK_ICON: IconSpec = { type: 'builtin', id: 'i-command' };

/** Slot the row reserves for a glyph; portraits fill it, SVGs sit inset. */
const ROW_ICON_SLOT = 24;
/** Builtin / fallback SVGs — small enough to clear a 24px circle's corners. */
const ROW_GLYPH_SIZE = 16;

/**
 * Geometry and highlight band of one action row, for whichever item primitive
 * the face is built on.
 *
 * A class string plus {@link ActionRowContent} rather than one component,
 * because the element itself cannot be shared: the search dropdown's rows are
 * `Combobox.Item`s and the Create menu's are `Menu.Item`s — different APG
 * patterns, as they should be (one is a filter-as-you-type listbox, the other a
 * menu button). What must not diverge is how a row *looks*, so that lives here
 * once. Both primitives already render `position: relative` (the `after` rule
 * needs it) and both mark the active row with `data-highlighted`.
 */
export const ACTION_ROW_CLASS =
  'group w-full min-h-10 items-center gap-2.5 rounded-none px-3 after:absolute after:inset-x-3 after:bottom-0 after:h-px after:bg-(--app-color-border) data-highlighted:bg-(--app-color-bg-muted)';

export interface ActionRowContentProps {
  icon?: IconSpec;
  /**
   * A node, not a string: the search face hands over a title with its matched
   * terms already wrapped in `<mark>`.
   */
  title: ReactNode;
  /** Drawn immediately after the title. The face does not interpret it. */
  titleAffix?: ReactNode;
  /**
   * A node, not a string: the search face hands over a subtitle with its
   * matched terms already wrapped in `<mark>` (username / path / remark).
   */
  subtitle?: ReactNode;
  /**
   * Small tag drawn immediately after the title — a command's category, a
   * message's date. It sits beside the name because it qualifies the name;
   * the row's far edge is for {@link trailing}.
   */
  badge?: string;
  /** Right-aligned slot — a shortcut chip, how to reach the row, or nothing. */
  trailing?: ReactNode;
}

function RowGlyph({ icon }: { icon?: IconSpec }): ReactNode {
  const spec = icon ?? ROW_FALLBACK_ICON;
  // Photos are `resource` without `mono`. Module launcher SVGs are also
  // `resource`, but they set `mono` so they tint like a builtin glyph — those
  // must shrink, not fill the circle (否则会被圆角裁角，看起来像没改过).
  const portrait = spec.type === 'resource' && spec.mono !== true;
  return (
    <span
      className={cn(
        'flex size-6 shrink-0 items-center justify-center',
        portrait && 'overflow-hidden rounded-full',
      )}
    >
      <Icon
        size={portrait ? ROW_ICON_SLOT : ROW_GLYPH_SIZE}
        spec={spec}
        className={cn(
          portrait && 'size-full rounded-full object-cover',
          icon ? undefined : 'text-(--app-color-text-faint)',
        )}
      />
    </span>
  );
}

/** The inside of an action row: glyph, two lines of text, trailing slot. */
export function ActionRowContent({
  icon,
  title,
  titleAffix,
  subtitle,
  badge,
  trailing,
}: ActionRowContentProps): ReactNode {
  return (
    <>
      <RowGlyph icon={icon} />
      <span className="flex min-w-0 flex-auto flex-col">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="flex min-w-0 items-center">
            <span className="truncate text-[13px] leading-5 font-medium">
              {title}
            </span>
            {titleAffix}
          </span>
          {/* Flips to the surface colour on a highlighted row: its resting fill
              is the same token as the highlight band, so it would otherwise
              dissolve into it exactly when the row is the one being looked at. */}
          {badge ? (
            <span className="shrink-0 rounded bg-(--app-color-bg-muted) px-1.5 text-[11px] leading-4 text-(--app-color-text-faint) group-data-highlighted:bg-(--app-color-bg-surface)">
              {badge}
            </span>
          ) : null}
        </span>
        {subtitle ? (
          <span className="truncate text-[11px] leading-4 text-(--app-color-text-faint)">
            {subtitle}
          </span>
        ) : null}
      </span>
      {trailing}
    </>
  );
}
